/**
 * Snugloo: "Add to cart" goes straight to checkout.
 *
 * Every product add-to-cart on the storefront is a <form action="/cart/add">
 * submit: the main product button, the sticky bar (it clicks the main button)
 * and the pack cards (they only set the quantity field). We catch that submit
 * on window in the capture phase, before Horizon's delegated handler on
 * document, so the cart drawer never opens.
 *
 * Buy-now semantics: lines already in the cart are set to 0 first, so a
 * shopper who backs out of checkout and clicks again gets exactly what they
 * picked, not 1 + 1 sets. Cart attributes and note (ad attribution) are kept.
 * Automatic discounts (2 sets / 3+ sets) still apply at checkout.
 *
 * A ticked Priority Shipping box (snippets/pl-upsell.liquid) is added as one
 * extra line after the product, whatever the number of sets.
 */
(() => {
  if (window.plDirectCheckout) return;
  window.plDirectCheckout = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const url = (path) => root + path;
  const BUSY = 'pl-dc-busy';
  let inFlight = false;

  const isProductAddForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    let path;
    try {
      path = new URL(form.action, location.href).pathname;
    } catch (e) {
      return false;
    }
    if (!/\/cart\/add\/?$/.test(path)) return false;
    // Leave upsell forms inside the cart drawer / cart page alone.
    if (form.closest('cart-drawer-component, cart-items-component, .cart-drawer, #cart-drawer')) return false;
    return !!form.querySelector('[name="id"]');
  };

  const buttonsFor = (form) => {
    const scope = form.closest('product-form-component') || form;
    const list = Array.from(scope.querySelectorAll('[ref="addToCartButton"], button[type="submit"][name="add"]'));
    document.querySelectorAll('sticky-add-to-cart [ref="addToCartButton"]').forEach((b) => list.push(b));
    return Array.from(new Set(list));
  };

  const setBusy = (buttons, busy) => {
    buttons.forEach((b) => {
      b.classList.toggle(BUSY, busy);
      if (busy) {
        b.setAttribute('aria-busy', 'true');
        b.dataset.plDcWasDisabled = b.disabled ? '1' : '';
        b.disabled = true;
      } else {
        b.removeAttribute('aria-busy');
        if (b.dataset.plDcWasDisabled !== undefined) b.disabled = b.dataset.plDcWasDisabled === '1';
        delete b.dataset.plDcWasDisabled;
      }
    });
  };

  const showError = (form, message) => {
    const text = message || 'Sorry, this item could not be added. Please try again.';
    const scope = form.closest('product-form-component') || form;
    const box = scope.querySelector('[ref="addToCartTextError"]');
    if (!box) {
      window.alert(text);
      return;
    }
    box.classList.remove('hidden');
    const textNode = Array.from(box.childNodes).reverse().find((n) => n.nodeType === Node.TEXT_NODE);
    if (textNode) textNode.textContent = text;
    else box.appendChild(document.createTextNode(text));
    setTimeout(() => box.classList.add('hidden'), 10000);
  };

  const json = async (response) => {
    let data = null;
    try {
      data = await response.json();
    } catch (e) {}
    if (!response.ok || (data && data.status && data.status >= 400)) {
      const err = new Error((data && (data.description || data.message)) || 'Request failed');
      err.isCartError = true;
      throw err;
    }
    return data;
  };

  const emptyCart = async () => {
    const cart = await fetch(url('cart.js'), { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(json);
    if (!cart || !cart.items || !cart.items.length) return;
    const updates = {};
    cart.items.forEach((item) => (updates[item.key] = 0));
    await fetch(url('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    }).then(json);
  };

  window.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!isProductAddForm(form)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (inFlight) return;
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const body = new FormData(form);
      body.delete('sections');
      body.delete('sections_url');
      if (!body.get('id')) return;
      const qty = parseInt(body.get('quantity'), 10);
      body.set('quantity', String(qty > 0 ? qty : 1));

      // Ticked add-ons (snippets/pl-upsell.liquid) next to this form, once per order.
      const scope = form.closest('.shopify-section') || document;

      // A pack of 2+ with its own item rows (blocks/pl-bundle.liquid) adds each row's
      // variant (two colors, two sizes...) instead of the selected variant x quantity.
      const pack = scope.querySelector('.pl-bundle input[type="radio"]:checked');
      const tier = pack && pack.closest('.pl-bundle__tier');
      const unitRows = tier ? Array.from(tier.querySelectorAll('[data-pl-unit]')) : [];
      let packItems = null;
      if (unitRows.length > 1) {
        const ids = unitRows.map((row) => Number(row.dataset.variantId)).filter(Boolean);
        if (ids.length !== unitRows.length) {
          showError(form, 'One of the items is not available in that combination. Please pick another option.');
          return;
        }
        const counts = new Map();
        ids.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
        packItems = Array.from(counts, ([id, quantity]) => ({ id, quantity }));
      }
      const upsellItems = Array.from(scope.querySelectorAll('.pl-upsell__check:checked'))
        .map((el) => {
          const sel = el.closest('.pl-upsell') && el.closest('.pl-upsell').querySelector('[data-pl-upsell-select]');
          return Number(el.dataset.plUpsellVariant || (sel && sel.value));
        })
        .filter(Boolean)
        .map((id) => ({ id, quantity: 1 }));

      inFlight = true;
      const buttons = buttonsFor(form);
      setBusy(buttons, true);

      try {
        await emptyCart();
        await fetch(url('cart/add.js'), packItems
          ? {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ items: packItems }),
            }
          : {
              method: 'POST',
              headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              credentials: 'same-origin',
              body,
            }
        ).then(json);
        if (upsellItems.length) {
          // Best effort: a failed upsell must not block the order itself.
          await fetch(url('cart/add.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ items: upsellItems }),
          })
            .then(json)
            .catch((e) => console.warn('[pl-direct-checkout] upsell not added:', e));
        }
        window.location.assign(url('checkout'));
      } catch (error) {
        inFlight = false;
        setBusy(buttons, false);
        showError(form, error && error.isCartError ? error.message : null);
      }
    },
    true
  );

  // Coming back from checkout with the Back button restores this page from
  // the back/forward cache with the buttons still busy; reset them.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    inFlight = false;
    document.querySelectorAll('.' + BUSY).forEach((b) => setBusy([b], false));
  });

  // Switching color can leave no size picked (Gray has no EU 34; the picker
  // hides sizes a color doesn't come in), which shows "Unavailable". Pick the
  // first size that color has instead.
  const fillEmptyOption = () => {
    document.querySelectorAll('.product-details fieldset.variant-option').forEach((fs) => {
      const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
      if (!inputs.length || inputs.some((i) => i.checked)) return;
      const pick = inputs.find((i) => i.dataset.optionAvailable === 'true') || inputs[0];
      pick.click();
    });
  };
  document.addEventListener('change', (event) => {
    if (!(event.target.closest && event.target.closest('.product-details fieldset.variant-option'))) return;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      fillEmptyOption();
      if (tries > 25) clearInterval(timer);
    }, 150);
  });

  // Round color swatches (.pl-swatchset) show only dots: name the picked color in the legend right away,
  // before Horizon's re-render of the picker arrives.
  document.addEventListener('change', (event) => {
    const input = event.target;
    const set = input && input.closest && input.closest('.pl-swatchset');
    const out = set && set.querySelector('[data-pl-swatch-value]');
    if (out && input.value) out.textContent = input.value;
  });

  // Picking a size or color in an add-on card ticks that card and shows that variant's photo.
  const showPick = (card, img) => {
    const pic = img && card.querySelector('.pl-upsell__img');
    if (pic) pic.src = img;
  };
  const tick = (card) => {
    const box = card.querySelector('.pl-upsell__check');
    if (box) box.checked = true;
  };
  document.addEventListener('change', (event) => {
    const sel = event.target.closest && event.target.closest('[data-pl-upsell-select]');
    const card = sel && sel.closest('.pl-upsell');
    if (!card) return;
    tick(card);
    const opt = sel.options[sel.selectedIndex];
    showPick(card, opt && opt.dataset.img);
  });
  document.addEventListener('click', (event) => {
    const chip = event.target.closest && event.target.closest('[data-pl-upsell-pick]');
    const card = chip && chip.closest('.pl-upsell');
    if (!card) return;
    event.preventDefault();
    card.querySelectorAll('[data-pl-upsell-pick]').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
    const box = card.querySelector('.pl-upsell__check');
    if (box) box.dataset.plUpsellVariant = chip.dataset.plUpsellPick;
    tick(card);
    showPick(card, chip.dataset.img);
  });
})();
